import { defineStore, acceptHMRUpdate } from "pinia";
import Api from "src/utils/api";
import { useProfileStore } from "./profileStore";
import Helper from "../utils/helper";
import Constants from "@/utils/constants";

export const usePermissionsStore = defineStore("permissions_store", {
  persist: true,
  state: () => ({
    permissions: {}
  }),
  getters: {
    /**
     * Gets the current permissions state
     * @returns {Object} The permissions object containing all permission levels and their associated data
     */
    getPermissions: (state) => state.permissions
  },
  actions: {
    /**
     * Fetches and sets permissions from the profile store
     * @returns {Promise<Object>} The permissions response from the API
     */
    async setPermissions() {
      const callback = (permissions) => {
        if (Helper.isValueExist(permissions)) {
          this.permissions = permissions;
        }
      };
      Api.Permissions.subscribeToPermissions(callback);
      const profileStore = useProfileStore();
      const response = await profileStore.getPermissions();
      this.permissions = response;
      return response;
    },

    /**
     * Gets combined permissions for a specific permission level and all higher levels
     * @param {string} permissionsLevel - The permission level to start from (READER, WRITER, APPROVER, ADMIN)
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @returns {Object} Combined permissions object with arrays for form, shop, region, company, and dashboard permissions
     */
    getCombinedPermissions(permissionsLevel, permissions = this.permissions) {
      // Define permission hierarchy in ascending order
      const permissionHierarchy = [
        Constants.enums.PERMISSION_LEVELS.READER.key,
        Constants.enums.PERMISSION_LEVELS.WRITER.key,
        Constants.enums.PERMISSION_LEVELS.APPROVER.key,
        Constants.enums.PERMISSION_LEVELS.ADMIN.key
      ];

      // Find the index of the requested permission level
      const requestedLevelIndex = permissionHierarchy.indexOf(permissionsLevel);

      if (requestedLevelIndex === -1) {
        // If permission level not found, return empty object
        return {};
      }

      // Get all permission levels from requested level and above
      const allowedLevels = permissionHierarchy.slice(requestedLevelIndex);

      // Initialize combined permissions object
      const combinedPermissions = {
        form: [],
        shop: [],
        dashboard: []
      };

      // Combine permissions from allowed levels
      allowedLevels.forEach((level) => {
        if (permissions[level]) {
          // Combine each permission type
          Object.keys(combinedPermissions).forEach((permissionType) => {
            if (
              permissions[level][permissionType] &&
              Array.isArray(permissions[level][permissionType])
            ) {
              combinedPermissions[permissionType].push(
                ...permissions[level][permissionType]
              );
            }
          });
        }
      });

      // Remove duplicates from each permission type
      Object.keys(combinedPermissions).forEach((permissionType) => {
        combinedPermissions[permissionType] = [
          ...new Set(combinedPermissions[permissionType])
        ];
      });

      combinedPermissions.region = [...(permissions.region || [])];
      combinedPermissions.company = [...(permissions.company || [])];

      return combinedPermissions;
    },
    /**
     * Core permission check: does the user hold `id` in `category` at `level`
     * (or higher, per the hierarchy)? The reserved ALL_FORMS grant always passes.
     * Single source of truth for the all-access bypass and for normalizing
     * dashboard entries (objects `{ id, ... }`) vs form/shop entries (id strings).
     * Reactive — reads `this.permissions` — so it can be used inside computeds.
     * @param {string} id - The id to check (form/dashboard/shop template id)
     * @param {string} [category="form"] - form | shop | dashboard | region | company
     * @param {string} [level="reader"] - Minimum permission level (hierarchy-aware)
     * @param {Object} [permissions=this.permissions] - Permissions object to use
     * @returns {boolean}
     */
    hasPermission(
      id,
      category = Constants.enums.PERMISSION_CATEGORIES.FORM.key,
      level = Constants.enums.PERMISSION_LEVELS.READER.key,
      permissions = this.permissions
    ) {
      const combined = this.getCombinedPermissions(level, permissions);
      const ids = (combined[category] || []).map((entry) =>
        typeof entry === "string" ? entry : entry?.id
      );
      return (
        ids.includes(Constants.enums.FORM_TEMPLATE_IDS.ALL_FORMS) ||
        ids.includes(id)
      );
    },
    hasAnyPermission(
      category = Constants.enums.PERMISSION_CATEGORIES.FORM.key,
      level = Constants.enums.PERMISSION_LEVELS.READER.key,
      permissions = this.permissions
    ) {
      const combined = this.getCombinedPermissions(level, permissions);
      return (combined[category] || []).length > 0;
    },
    /**
     * Reader-level convenience wrapper — "can the user see / access this id?".
     * @param {string} id
     * @param {string} [category="form"]
     * @param {Object} [permissions=this.permissions]
     * @returns {boolean}
     */
    canRead(
      id,
      category = Constants.enums.PERMISSION_CATEGORIES.FORM.key,
      permissions = this.permissions
    ) {
      return this.hasPermission(
        id,
        category,
        Constants.enums.PERMISSION_LEVELS.READER.key,
        permissions
      );
    },
    /**
     * Writer-level (or above) permission check for a specific id. Delegates to
     * hasPermission; the reserved ALL_FORMS grant always passes.
     * @param {string} formTemplateId - The id to check (e.g. a form_templates id)
     * @param {string} [category="dashboard"] - Category: form | shop | dashboard
     * @param {Object} [permissions=this.permissions] - Permissions object to use
     * @returns {boolean} True if the user can write for that id
     */
    canWrite(
      formTemplateId,
      category = Constants.enums.PERMISSION_CATEGORIES.DASHBOARD.key,
      permissions = this.permissions
    ) {
      return this.hasPermission(
        formTemplateId,
        category,
        Constants.enums.PERMISSION_LEVELS.WRITER.key,
        permissions
      );
    },
    /**
     * Fetches and filters companies based on user permissions
     * @param {Object} queryParams - Query parameters for the API request
     * @param {string} permissionsLevel - The permission level to check against
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @returns {Promise<Object|Array>} API response with filtered companies or empty array on error
     */
    async getCompanies(
      queryParams,
      permissionsLevel,
      permissions = this.permissions
    ) {
      try {
        // Fetch all companies from API
        const response = await Api.Permissions.getMasterCompanies(queryParams);

        if (!response || !response.data || !Array.isArray(response.data)) {
          return [];
        }

        if (permissionsLevel === Constants.enums.PERMISSION_LEVELS.NONE.key) {
          return response;
        }

        // Filter companies based on permissions (only by company ID)
        const filteredCompanies = response.data.filter((company) => {
          // Check if user has permission for this company's ID
          const hasCompanyPermission = this.hasPermission(
            company.id,
            Constants.enums.PERMISSION_CATEGORIES.COMPANY.key,
            permissionsLevel,
            permissions
          );

          return hasCompanyPermission;
        });

        response.data = filteredCompanies;

        return response;
      } catch (error) {
        console.error("Error fetching companies:", error);
        return [];
      }
    },
    /**
     * Fetches and filters regions based on user permissions
     * @param {Object} queryParams - Query parameters for the API request
     * @param {string} permissionsLevel - The permission level to check against
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @param {Object} [options] - `records` filters an already-fetched list instead of hitting the API;
     *                             `offlineFirst` picks the transport when it does fetch
     * @returns {Promise<Object|Array>} API response with filtered regions or empty array on error
     */
    async getRegions(
      queryParams,
      permissionsLevel,
      permissions = this.permissions,
      options = {}
    ) {
      const { records = null, offlineFirst = true } = options;
      try {
        // Fetch all regions from API, unless the caller supplied the list
        const response = records
          ? { data: records }
          : await Api.Permissions.getMasterRegions(queryParams, offlineFirst);

        if (!response || !response.data || !Array.isArray(response.data)) {
          return [];
        }

        if (permissionsLevel === Constants.enums.PERMISSION_LEVELS.NONE.key) {
          return response;
        }

        // Filter regions based on permissions
        const filteredRegions = response.data.filter((region) => {
          // Check if user has permission for this region's ID
          const hasRegionPermission = this.hasPermission(
            region.id,
            Constants.enums.PERMISSION_CATEGORIES.REGION.key,
            permissionsLevel,
            permissions
          );

          // Return true if user has any of the required permissions
          return hasRegionPermission;
        });

        response.data = filteredRegions;

        return response;
      } catch (error) {
        console.error("Error fetching regions:", error);
        return [];
      }
    },
    /**
     * Fetches and filters shops based on user permissions
     * @param {Object} queryParams - Query parameters for the API request
     * @param {string} permissionsLevel - The permission level to check against
     * @param {Object} [permissions=this.permissions] - The permissions object to use (defaults to store permissions)
     * @param {Object} [options] - `records` filters an already-fetched list instead of hitting the API;
     *                             `offlineFirst` picks the transport when it does fetch
     * @returns {Promise<Object|Array>} API response with filtered shops or empty array on error
     */
    async getShops(
      queryParams,
      permissionsLevel,
      permissions = this.permissions,
      options = {}
    ) {
      const { records = null, offlineFirst = true } = options;
      try {
        // Fetch all shops from API, unless the caller supplied the list
        const response = records
          ? { data: records }
          : await Api.Permissions.getMasterShops(queryParams, offlineFirst);

        if (!response || !response.data || !Array.isArray(response.data)) {
          return [];
        }

        if (permissionsLevel === Constants.enums.PERMISSION_LEVELS.NONE.key) {
          return response;
        }

        // Filter shops based on permissions
        const filteredShops = response.data.filter((shop) => {
          // Check if user has permission for this specific shop
          const hasShopPermission = this.hasPermission(
            shop.id,
            Constants.enums.PERMISSION_CATEGORIES.SHOP.key,
            permissionsLevel,
            permissions
          );

          // Return true if user has any of the required permissions
          return hasShopPermission;
        });

        response.data = filteredShops;

        return response;
      } catch (error) {
        console.error("Error fetching shops:", error);
        return [];
      }
    }
  },
  $reset() {}
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePermissionsStore, import.meta.hot));
}
